import { useGetChannelVideosAnalyticsApiV1Suspense } from "@/api/__generated__/api.schema.ts";
import type { VideoSummaryAnalytics } from "@/api/__generated__/models/videoSummaryAnalytics.ts";
import type { VideoSummaryVideo } from "@/api/__generated__/models/videoSummaryVideo.ts";
import { ExternalLink } from "@/components/external-link.tsx";
import { externalUrls } from "@/constants/url.ts";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { formatCurrency, formatDateString, formatPercentage } from "@/utils/format.ts";
import {
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Thumbnail,
  ThumbnailFallback,
  ThumbnailImage,
  Typography,
} from "@backpac/monica";
import { Suspense, useEffect, useState } from "react";
import { useChannelAnalytics } from "../../hooks/use-channel-analytics.tsx";
import { fetchYoutubeVideo } from "../../hooks/use-youtube-video.ts";
import { SORT_OPTIONS, VideoSort, VideoSortOption } from "./video-sort.tsx";

interface VideoAnalyticsProps {
  className?: string;
}

export function VideoAnalytics(props: VideoAnalyticsProps) {
  const isMobile = useIsMobile();
  const { channelId, channels } = useChannelAnalytics();

  if (channels.length === 0) {
    // This represents the case the channel is not connected
    return <VideoAnalytics.Empty />;
  }

  if (channelId == null) {
    return <VideoAnalytics.Pending />;
  }

  return (
    <>
      <Suspense fallback={<VideoAnalytics.Pending />}>
        {isMobile ? (
          <VideoAnalyticsMobile channelId={channelId} {...props} />
        ) : (
          <VideoAnalyticsDesktop channelId={channelId} {...props} />
        )}
      </Suspense>
    </>
  );
}

function VideoAnalyticsDesktop({ className, channelId }: VideoAnalyticsProps & { channelId: string }) {
  const workspaceId = useWorkspaceId();
  const [sortOption, setSortOption] = useState<VideoSortOption>(VideoSortOption.RECENT_UPLOADS);
  const { data } = useGetChannelVideosAnalyticsApiV1Suspense(workspaceId, channelId!, {
    sort: SORT_OPTIONS[sortOption].value.sort,
    order: SORT_OPTIONS[sortOption].value.order,
  });

  const videos = data?.data.items ?? [];

  return (
    <div className={className}>
      <Table className="caption-top">
        <TableCaption className="mt-0 mb-6">
          <div className="flex items-center justify-between px-6">
            <Typography size="lg" font="unbounded" color="black">
              Contents
            </Typography>
            <VideoSort value={sortOption} onChange={setSortOption} />
          </div>
        </TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">Shorts</TableHead>
            <TableHead>Track used</TableHead>
            <TableHead className="text-right">Total views</TableHead>
            <TableHead className="text-right">Premium sub. views</TableHead>
            <TableHead className="text-right">Premium rate</TableHead>
            <TableHead className="text-right">Estimated payout</TableHead>
            <TableHead className="pr-6 text-right">RPM</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {videos.length === 0 ? (
            <VideoAnalytics.EmptyVideosRow />
          ) : (
            videos.map((video) => <VideoAnalyticsDesktop.Row key={video.id} {...video} />)
          )}
        </TableBody>
      </Table>
    </div>
  );
}

VideoAnalyticsDesktop.Row = function VideoTableRow(video: VideoSummaryAnalytics) {
  const premiumRate = formatPercentage((video.metrics.premiumSubscriberViews / video.metrics.totalViews) * 100);
  const [videoInfo, setVideoInfo] = useState<Partial<VideoSummaryVideo> | null>(video.video);

  // This is to fetch the video info from youtube oembed API, in case the video info is not available in the DB
  // TODO: Remove this once the video info must be available in the DB
  useEffect(() => {
    if (video.video == null) {
      fetchYoutubeVideo(video.id).then((_video) => {
        setVideoInfo(_video);
      });
    }
  }, [video.video, video.id]);

  return (
    <TableRow>
      <TableCell className="align-top p-3 pl-6">
        <ExternalLink to={`https://www.youtube.com/shorts/${video.id}`}>
          <div className="flex gap-3 max-w-[240px]">
            <Thumbnail className="w-[41px] h-[74px] rounded-[4px]" variant="square">
              <ThumbnailImage src={videoInfo?.thumbnailUrl} alt={videoInfo?.title} className="object-cover" />
              <ThumbnailFallback>{videoInfo?.title?.slice(0, 2)}</ThumbnailFallback>
            </Thumbnail>
            <div className="flex flex-col gap-1.5 mt-1">
              <Typography size="xs" weight={600} color="textPrimary" className="line-clamp-2">
                {videoInfo?.title ?? "N/A"}
              </Typography>
              {/* TODO: Add video published date. currently it doesn't support published date */}
              <Typography size="2xs" color="textMuted">
                {videoInfo?.publishedAt ? formatDateString(videoInfo.publishedAt) : "N/A"}
              </Typography>
            </div>
          </div>
        </ExternalLink>
      </TableCell>
      <TableCell className="align-top p-3">
        <div className="flex gap-3">
          <Thumbnail variant="square" className="size-8 rounded-[4px]">
            <ThumbnailImage
              src={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${video.music.isrc}.avif`}
              alt={video.music.title}
            />
            <ThumbnailFallback>{video.music.title?.slice(0, 2)}</ThumbnailFallback>
          </Thumbnail>
          <Typography size="xs" weight={400} className="mt-1">
            {video.music.title}
          </Typography>
        </div>
      </TableCell>
      <TableCell className="align-top text-right p-3">
        <Typography size="xs" weight={400} color="textPrimary">
          {video.metrics.totalViews.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell className="align-top text-right p-3">
        <Typography size="xs" weight={400} color="textPrimary">
          {video.metrics.premiumSubscriberViews.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell className="align-top text-right p-3">
        <Typography size="xs" weight={400} color="textPrimary">
          {premiumRate}
        </Typography>
      </TableCell>
      <TableCell className="align-top text-right p-3">
        <Typography size="xs" weight={400} color="textPrimary">
          {formatCurrency(video.metrics.estimatedPayouts)}
        </Typography>
      </TableCell>
      <TableCell className="align-top text-right p-3 pr-6">
        <Typography size="xs" weight={400} color="textPrimary">
          {formatCurrency(video.metrics.rpm)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

function VideoAnalyticsMobile({ className, channelId }: VideoAnalyticsProps & { channelId: string }) {
  const workspaceId = useWorkspaceId();
  const [sortOption, setSortOption] = useState<VideoSortOption>(VideoSortOption.RECENT_UPLOADS);
  const {
    data: {
      data: { items },
    },
  } = useGetChannelVideosAnalyticsApiV1Suspense(workspaceId, channelId!, {
    sort: SORT_OPTIONS[sortOption].value.sort,
    order: SORT_OPTIONS[sortOption].value.order,
  });

  const videos = items ?? [];

  return (
    <div className={cn("space-y-[40px]", className)}>
      <Table className="caption-top">
        <TableCaption>
          <div className="flex items-center justify-between px-6">
            <Typography size="lg" font="unbounded" color="black">
              Content
            </Typography>
            <VideoSort value={sortOption} onChange={setSortOption} />
          </div>
        </TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">{""}</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          {videos.length === 0 ? (
            <VideoAnalytics.EmptyVideosRow />
          ) : (
            videos.map((video) => <VideoAnalyticsMobile.Row key={video.id} {...video} />)
          )}
        </TableBody>
      </Table>
    </div>
  );
}

VideoAnalyticsMobile.Row = function VideoTableRow(video: VideoSummaryAnalytics) {
  const premiumRate = formatPercentage((video.metrics.premiumSubscriberViews / video.metrics.totalViews) * 100);
  const [videoInfo, setVideoInfo] = useState<Partial<VideoSummaryVideo> | null>(video.video);

  // This is to fetch the video info from youtube oembed API, in case the video info is not available in the DB
  // TODO: Remove this once the video info must be available in the DB
  useEffect(() => {
    if (video.video == null) {
      fetchYoutubeVideo(video.id).then((_video) => {
        setVideoInfo(_video);
      });
    }
  }, [video.video, video.id]);

  return (
    <TableRow>
      <TableCell className="p-6">
        <div className="flex flex-col gap-3">
          <div className="flex gap-4">
            <ExternalLink to={`https://www.youtube.com/shorts/${video.id}`}>
              <Thumbnail className="w-[70px] h-[120px] rounded-md" variant="square">
                <ThumbnailImage src={videoInfo?.thumbnailUrl} alt={videoInfo?.title} className="object-cover" />
                <ThumbnailFallback>{videoInfo?.title?.slice(0, 2)}</ThumbnailFallback>
              </Thumbnail>
            </ExternalLink>
            <div className="flex flex-col mt-0.5">
              <Typography size="xs" weight={600} color="textPrimary" className="line-clamp-2 leading-1.2">
                {videoInfo?.title ?? "N/A"}
              </Typography>
              {/* TODO: Add video published date. currently it doesn't support published date */}
              <Typography size="xs" color="textMuted" className="mt-1.5">
                {videoInfo?.publishedAt ? formatDateString(videoInfo.publishedAt) : "N/A"}
              </Typography>
              <div className="flex gap-3 mt-4">
                <Thumbnail variant="square" className="size-8">
                  <ThumbnailImage
                    src={`${externalUrls.MUSIC_RESOURCE_BASE_URL}/${video.music.isrc}.avif`}
                    alt={video.music.title}
                  />
                  <ThumbnailFallback>{video.music.title?.slice(0, 2)}</ThumbnailFallback>
                </Thumbnail>
                <Typography size="xs" weight={400} className="mt-1">
                  {video.music.title}
                </Typography>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Typography size="xs" weight={400}>
                Total Views
              </Typography>
              <Typography size="xs" weight={600} color="textPrimary">
                {video.metrics.totalViews.toLocaleString()}
              </Typography>
            </div>
            <div className="flex items-center justify-between">
              <Typography size="xs" weight={400}>
                Premium Sub. Views
              </Typography>
              <Typography size="xs" weight={600} color="textPrimary">
                {video.metrics.premiumSubscriberViews.toLocaleString()}
              </Typography>
            </div>
            <div className="flex items-center justify-between">
              <Typography size="xs" weight={400}>
                Premium Rate
              </Typography>
              <Typography size="xs" weight={600} color="textPrimary">
                {premiumRate}
              </Typography>
            </div>
            <div className="flex items-center justify-between">
              <Typography size="xs" weight={400}>
                Est. Payouts
              </Typography>
              <Typography size="xs" weight={600} color="textPrimary">
                {formatCurrency(video.metrics.estimatedPayouts)}
              </Typography>
            </div>
            <div className="flex items-center justify-between">
              <Typography size="xs" weight={400}>
                RPM
              </Typography>
              <Typography size="xs" weight={600} color="textPrimary">
                {formatCurrency(video.metrics.rpm)}
              </Typography>
            </div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

VideoAnalytics.Pending = function VideoTablePending() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Table className="caption-top">
        <TableCaption className="mt-0 mb-6">
          <div className="flex items-center justify-between px-6">
            <Typography size="lg" font="unbounded" color="black">
              Contents
            </Typography>
            <VideoSort value={VideoSortOption.RECENT_UPLOADS} onChange={() => {}} />
          </div>
        </TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">{""}</TableHead>
          </TableHeadRow>
        </TableHeader>
        <Skeleton.TableBoday />
      </Table>
    );
  }

  return (
    <Table className="caption-top">
      <TableCaption className="mt-0 mb-6">
        <div className="flex items-center justify-between px-6">
          <Typography size="lg" font="unbounded" color="black">
            Contents
          </Typography>
          <VideoSort value={VideoSortOption.RECENT_UPLOADS} onChange={() => {}} />
        </div>
      </TableCaption>
      <TableHeader>
        <TableHeadRow>
          <TableHead className="pl-6">Shorts</TableHead>
          <TableHead>Track used</TableHead>
          <TableHead>Total views</TableHead>
          <TableHead>Premium sub. views</TableHead>
          <TableHead>Premium rate</TableHead>
          <TableHead>Est. payouts</TableHead>
          <TableHead>RPM</TableHead>
        </TableHeadRow>
      </TableHeader>
      <Skeleton.TableBoday colSpan={7} />
    </Table>
  );
};

VideoAnalytics.Empty = function VideoAnalyticsEmpty() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Table className="caption-top">
        <TableCaption className="mt-0 mb-6">
          <div className="flex items-center justify-between px-6">
            <Typography size="lg" font="unbounded" color="black">
              Contents
            </Typography>
            <VideoSort value={VideoSortOption.RECENT_UPLOADS} onChange={() => {}} />
          </div>
        </TableCaption>
        <TableHeader>
          <TableHeadRow>
            <TableHead className="pl-6">{""}</TableHead>
          </TableHeadRow>
        </TableHeader>
        <TableBody>
          <VideoAnalytics.EmptyVideosRow />
        </TableBody>
      </Table>
    );
  }

  return (
    <Table className="caption-top">
      <TableCaption className="mt-0 mb-6">
        <div className="flex items-center justify-between px-6">
          <Typography size="lg" font="unbounded" color="black">
            Contents
          </Typography>
          <VideoSort value={VideoSortOption.RECENT_UPLOADS} onChange={() => {}} />
        </div>
      </TableCaption>
      <TableHeader>
        <TableHeadRow>
          <TableHead className="pl-6">Shorts</TableHead>
          <TableHead>Track used</TableHead>
          <TableHead>Total views</TableHead>
          <TableHead>Premium sub. views</TableHead>
          <TableHead>Premium rate</TableHead>
          <TableHead>Est. payouts</TableHead>
          <TableHead>RPM</TableHead>
        </TableHeadRow>
      </TableHeader>
      <TableBody>
        <VideoAnalytics.EmptyVideosRow />
      </TableBody>
    </Table>
  );
};

VideoAnalytics.EmptyVideosRow = function VideoTableEmptyFavorites() {
  const isMobile = useIsMobile();

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={isMobile ? 1 : 7} className="text-center">
        <div className="space-y-3 py-12">
          <div className="text-white">
            <Icon name="youtube-bold" bgColor="brandTangerine" iconSize="24px" />
          </div>
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No contents yet
            </Typography>
            <Typography size="xs" color="textMuted">
              YouTube Shorts using our music will appear here
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};
